<template>
  <div v-if="inline">
    <v-card
      v-if="ohje"
      outlined
      :class="$vuetify.breakpoint.xsOnly ? 'rounded-lg elevation-0' : 'rounded-xl elevation-2'"
      class="evy-guide-card"
    >
      <v-card-title
        v-if="!hideTitle"
        :class="$vuetify.breakpoint.xsOnly ? 'py-2 px-2' : 'py-3 px-4'"
        class="primary white--text d-flex align-center"
      >
        <v-icon color="white" :small="$vuetify.breakpoint.xsOnly" class="mr-1">mdi-ambulance</v-icon>
        <span
          class="white--text"
          :class="$vuetify.breakpoint.xsOnly ? 'text-caption font-weight-bold' : 'text-subtitle-1 font-weight-bold'"
        >
          ENSIVASTE PIKAKERTAUS: {{ ohje.title }} ({{ activeCode }})
        </span>
        <v-spacer />
        <v-chip
          v-if="ohje.urgency"
          :x-small="$vuetify.breakpoint.xsOnly"
          :small="!$vuetify.breakpoint.xsOnly"
          color="white"
          text-color="primary"
          class="font-weight-bold ml-1"
        >
          {{ $vuetify.breakpoint.xsOnly ? ohje.urgency : 'Kiireellisyys: ' + ohje.urgency }}
        </v-chip>
      </v-card-title>

      <v-alert
        dense
        text
        color="primary"
        :class="$vuetify.breakpoint.xsOnly ? 'ma-1 mb-2 pa-2 text-caption rounded' : 'ma-3 mb-2 text-body-2 rounded-lg'"
        class="font-weight-medium"
        icon="mdi-flash"
      >
        {{ ohje.shortSummary }}
      </v-alert>

      <!-- PIKA-PIKA-PIKA: HENGENPELASTAVAT TOIMET ENNEN "MITÄ KYSYTÄÄN" -->
      <v-card
        v-if="ohje.pikaPikaPika && ohje.pikaPikaPika.length > 0"
        outlined
        :class="$vuetify.breakpoint.xsOnly ? 'mx-1 mb-2 pa-2 rounded-lg' : 'mx-3 mb-3 pa-3 rounded-xl'"
        class="red lighten-5 elevation-1"
        style="border: 2px solid #6c0000 !important;"
      >
        <div class="d-flex align-center justify-space-between flex-wrap mb-1">
          <div class="d-flex align-center">
            <v-icon color="primary" :small="$vuetify.breakpoint.xsOnly" class="mr-1">mdi-lightning-bolt-circle</v-icon>
            <span :class="$vuetify.breakpoint.xsOnly ? 'text-caption font-weight-black' : 'text-subtitle-2 font-weight-black'" class="primary--text text-uppercase">
              Pika-pika-pika: Hengenpelastavat ensitoimet
            </span>
          </div>
          <v-chip
            x-small
            color="primary"
            dark
            class="font-weight-black my-1"
          >
            <v-icon left x-small>mdi-timer-sand-full</v-icon>
            KRIITTISET ENSIMINUUTIT
          </v-chip>
        </div>

        <v-list dense class="pa-0 transparent">
          <v-list-item
            v-for="(point, pIdx) in ohje.pikaPikaPika"
            :key="pIdx"
            :class="[
              $vuetify.breakpoint.xsOnly ? 'px-1 py-1 rounded mb-1' : 'px-2 py-1 rounded-lg mb-1',
              $vuetify.theme.dark ? 'grey darken-3' : 'white',
            ]"
            class="d-flex align-start"
            style="border-left: 4px solid #6c0000; box-shadow: 0 1px 2px rgba(0,0,0,0.05);"
          >
            <v-avatar
              :size="$vuetify.breakpoint.xsOnly ? 18 : 22"
              color="primary"
              :class="$vuetify.breakpoint.xsOnly ? 'mr-1 mt-1 text-caption' : 'mr-2 mt-1 text-caption'"
              class="white--text font-weight-black shrink"
            >
              {{ pIdx + 1 }}
            </v-avatar>
            <v-list-item-content class="py-0">
              <span
                :class="[
                  $vuetify.breakpoint.xsOnly ? 'text-caption font-weight-bold' : 'text-body-2 font-weight-bold',
                  $vuetify.theme.dark ? 'white--text' : 'grey--text text--darken-4',
                ]"
              >
                {{ point }}
              </span>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>

      <v-card-text :class="$vuetify.breakpoint.xsOnly ? 'pa-1 pt-1' : 'pt-2'">
        <v-tabs
          v-model="activeTab"
          :background-color="$vuetify.theme.dark ? '#252525' : 'grey lighten-4'"
          color="primary"
          grow
          :class="$vuetify.breakpoint.xsOnly ? 'rounded mb-2' : 'rounded-lg mb-3'"
          show-arrows
        >
          <v-tab :class="$vuetify.breakpoint.xsOnly ? 'px-2 text-caption font-weight-bold' : 'font-weight-bold'">
            <v-icon :left="!$vuetify.breakpoint.xsOnly" small color="primary"
              >mdi-comment-question-outline</v-icon
            >
            Mitä kysytään
          </v-tab>
          <v-tab :class="$vuetify.breakpoint.xsOnly ? 'px-2 text-caption font-weight-bold' : 'font-weight-bold'">
            <v-icon :left="!$vuetify.breakpoint.xsOnly" small :color="$vuetify.theme.dark ? 'deep-purple lighten-2' : 'deep-purple'">mdi-stethoscope</v-icon>
            Mitä mitataan
          </v-tab>
          <v-tab :class="$vuetify.breakpoint.xsOnly ? 'px-2 text-caption font-weight-bold' : 'font-weight-bold'">
            <v-icon :left="!$vuetify.breakpoint.xsOnly" small :color="$vuetify.theme.dark ? 'red lighten-2' : 'red darken-1'">mdi-hand-heart</v-icon>
            Mitä tehdään
          </v-tab>
          <v-tab v-if="hasMedication" :class="$vuetify.breakpoint.xsOnly ? 'px-2 text-caption font-weight-bold' : 'font-weight-bold'">
            <v-icon :left="!$vuetify.breakpoint.xsOnly" small :color="$vuetify.theme.dark ? 'orange lighten-1' : 'orange darken-2'">mdi-pill</v-icon>
            Lääkkeet
          </v-tab>
          <v-tab :class="$vuetify.breakpoint.xsOnly ? 'px-2 text-caption font-weight-bold' : 'font-weight-bold'">
            <v-icon :left="!$vuetify.breakpoint.xsOnly" small :color="$vuetify.theme.dark ? 'amber lighten-1' : 'amber darken-3'"
              >mdi-shield-alert-outline</v-icon
            >
            Turvallisuus
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" touchless>
          <!-- 1. MITÄ KYSYTÄÄN -->
          <v-tab-item>
            <v-sheet :class="$vuetify.breakpoint.xsOnly ? 'pa-1' : 'pa-2'">
              <div
                :class="$vuetify.breakpoint.xsOnly ? 'text-caption font-weight-bold mb-1' : 'text-subtitle-2 font-weight-bold mb-2'"
                class="primary--text d-flex align-center"
              >
                <v-icon small color="primary" class="mr-1"
                  >mdi-account-voice</v-icon
                >
                Esitiedot, oireet ja potilaan/omaisen haastattelu
              </div>
              <v-list dense class="pa-0">
                <v-list-item
                  v-for="(item, idx) in ohje.mitaKysytaan"
                  :key="idx"
                  :class="$vuetify.breakpoint.xsOnly ? 'px-1 py-1 rounded mb-1' : 'px-2 rounded-lg mb-1'"
                  class="grey lighten-5"
                >
                  <v-list-item-icon :class="$vuetify.breakpoint.xsOnly ? 'mr-1 my-1' : 'mr-2 my-2'">
                    <v-icon small color="primary"
                      >mdi-help-circle-outline</v-icon
                    >
                  </v-list-item-icon>
                  <v-list-item-content :class="$vuetify.breakpoint.xsOnly ? 'py-0' : 'py-1'">
                    <span :class="$vuetify.breakpoint.xsOnly ? 'text-caption' : 'text-body-2'" class="font-weight-medium">{{
                      item
                    }}</span>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-sheet>
          </v-tab-item>

          <!-- 2. MITÄ MITATAAN -->
          <v-tab-item>
            <v-sheet :class="$vuetify.breakpoint.xsOnly ? 'pa-1' : 'pa-2'">
              <div
                :class="$vuetify.breakpoint.xsOnly ? 'text-caption font-weight-bold mb-1' : 'text-subtitle-2 font-weight-bold mb-2'"
                class="deep-purple--text d-flex align-center"
              >
                <v-icon small color="deep-purple" class="mr-1"
                  >mdi-speedometer</v-icon
                >
                Vitaalielintoiminnot, hälyttävät rajat ja tutkimukset
              </div>
              <v-list dense class="pa-0">
                <v-list-item
                  v-for="(item, idx) in ohje.mitaMitataan"
                  :key="idx"
                  :class="$vuetify.breakpoint.xsOnly ? 'px-1 py-1 rounded mb-1' : 'px-2 rounded-lg mb-1'"
                  class="grey lighten-5"
                >
                  <v-list-item-icon :class="$vuetify.breakpoint.xsOnly ? 'mr-1 my-1' : 'mr-2 my-2'">
                    <v-icon small color="deep-purple"
                      >mdi-checkbox-marked-circle-outline</v-icon
                    >
                  </v-list-item-icon>
                  <v-list-item-content :class="$vuetify.breakpoint.xsOnly ? 'py-0' : 'py-1'">
                    <span
                      :class="[
                        $vuetify.breakpoint.xsOnly ? 'text-caption' : 'text-body-2',
                        item.startsWith('-')
                          ? 'pl-2 font-italic'
                          : 'font-weight-medium'
                      ]"
                    >
                      {{ item }}
                    </span>
                  </v-list-item-content>
                </v-list-item>
              </v-list>

              <!-- GCS ja FPSS pikapainikkeet -->
              <div class="d-flex flex-wrap align-center mt-2">
                <v-btn
                  text
                  :small="!$vuetify.breakpoint.xsOnly"
                  :x-small="$vuetify.breakpoint.xsOnly"
                  color="primary"
                  class="font-weight-bold mr-2 mb-1"
                  @click="showGcsDialog = true"
                >
                  <v-icon left small>mdi-calculator</v-icon>
                  GCS-laskuri
                </v-btn>

                <v-btn
                  :text="!isStroke"
                  :outlined="isStroke"
                  :small="!$vuetify.breakpoint.xsOnly"
                  :x-small="$vuetify.breakpoint.xsOnly"
                  :color="isStroke ? 'red darken-4' : 'primary'"
                  :class="[
                    'font-weight-bold mb-1',
                    isStroke ? 'red lighten-5 font-weight-black' : ''
                  ]"
                  @click="showFpssDialog = true"
                >
                  <v-icon left small :color="isStroke ? 'red darken-4' : 'primary'">mdi-brain</v-icon>
                  FPSS-aivohalvausmittari (0–8 p)
                </v-btn>
              </div>
            </v-sheet>
          </v-tab-item>

          <!-- 3. MITÄ TEHDÄÄN -->
          <v-tab-item>
            <v-sheet :class="$vuetify.breakpoint.xsOnly ? 'pa-1' : 'pa-2'">
              <div
                :class="$vuetify.breakpoint.xsOnly ? 'text-caption font-weight-bold mb-1' : 'text-subtitle-2 font-weight-bold mb-2'"
                class="red--text text--darken-2 d-flex align-center"
              >
                <v-icon small color="red darken-2" class="mr-1"
                  >mdi-run-fast</v-icon
                >
                Ensiarvio, asentohoito ja välittömät toimenpiteet (ABCDE)
              </div>
              <v-list dense class="pa-0">
                <v-list-item
                  v-for="(item, idx) in ohje.mitaTehdaan"
                  :key="idx"
                  :class="$vuetify.breakpoint.xsOnly ? 'px-1 py-1 rounded mb-1' : 'px-2 rounded-lg mb-1'"
                  class="grey lighten-5"
                >
                  <v-list-item-icon :class="$vuetify.breakpoint.xsOnly ? 'mr-1 my-1' : 'mr-2 my-2'">
                    <v-icon small color="red darken-2"
                      >mdi-chevron-right-circle</v-icon
                    >
                  </v-list-item-icon>
                  <v-list-item-content :class="$vuetify.breakpoint.xsOnly ? 'py-0' : 'py-1'">
                    <span
                      :class="[
                        $vuetify.breakpoint.xsOnly ? 'text-caption' : 'text-body-2',
                        {
                          'font-weight-bold red--text text--darken-4':
                            item.includes('PPE') ||
                            item.includes('ADRENALIINI') ||
                            item.includes('ELVYTYS'),
                          'pl-2':
                            item.startsWith('   -') || item.startsWith('   *'),
                        }
                      ]"
                    >
                      {{ item }}
                    </span>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-sheet>
          </v-tab-item>

          <!-- 4. LÄÄKKEET -->
          <v-tab-item v-if="hasMedication">
            <v-sheet :class="$vuetify.breakpoint.xsOnly ? 'pa-1' : 'pa-2'">
              <div
                :class="$vuetify.breakpoint.xsOnly ? 'text-caption font-weight-bold mb-1' : 'text-subtitle-2 font-weight-bold mb-2'"
                class="primary--text d-flex align-center"
              >
                <v-icon small color="primary" class="mr-1">mdi-pill</v-icon>
                Tähän tilanteeseen liittyvät ensivastelääkkeet ja lääkehappi
              </div>

              <!-- Tehtäväluokan happisuositus Lääkkeet-välilehdellä -->
              <v-card
                v-if="currentHappisuositus"
                outlined
                :class="$vuetify.breakpoint.xsOnly ? 'pa-2 rounded-lg mb-2' : 'pa-3 rounded-xl mb-3'"
                class="red lighten-5 d-flex align-center justify-space-between flex-wrap"
                style="border: 2px solid #6c0000 !important;"
              >
                <div class="d-flex align-center mr-2 my-1">
                  <v-icon color="primary" :small="$vuetify.breakpoint.xsOnly" class="mr-2">mdi-face-mask</v-icon>
                  <div>
                    <div :class="$vuetify.breakpoint.xsOnly ? 'text-caption font-weight-bold' : 'text-subtitle-2 font-weight-bold'" class="primary--text">
                      Lääkehappi: {{ currentHappisuositus.maski }}
                    </div>
                    <div class="text-caption grey--text text--darken-4">
                      {{ currentHappisuositus.ohje }}
                    </div>
                  </div>
                </div>
                <v-chip
                  :x-small="$vuetify.breakpoint.xsOnly"
                  :small="!$vuetify.breakpoint.xsOnly"
                  color="primary"
                  dark
                  class="font-weight-black my-1 ml-auto"
                >
                  <v-icon left x-small>mdi-speedometer</v-icon>
                  Virtaus: {{ currentHappisuositus.virtaus }}
                </v-chip>
              </v-card>

              <v-row dense>
                <v-col
                  v-for="(med, idx) in relevantMedications"
                  :key="idx"
                  cols="12"
                  :class="$vuetify.breakpoint.xsOnly ? 'pa-1' : ''"
                >
                  <v-card outlined :class="$vuetify.breakpoint.xsOnly ? 'rounded-lg pa-2 mb-1' : 'rounded-lg pa-3 mb-2'" class="grey lighten-5">
                    <div class="d-flex align-center justify-space-between mb-1">
                      <span
                        :class="$vuetify.breakpoint.xsOnly ? 'text-caption font-weight-bold' : 'text-subtitle-2 font-weight-bold'"
                        class="primary--text"
                      >
                        {{ med.name }}
                      </span>
                      <v-chip
                        x-small
                        color="primary"
                        dark
                        class="font-weight-bold"
                      >
                        {{ med.dose }}
                      </v-chip>
                    </div>
                    <div class="text-caption font-weight-medium mb-1">
                      <strong>Antotapa:</strong> {{ med.route }}
                    </div>
                    <div
                      v-if="med.name.includes('happi')"
                      :class="$vuetify.breakpoint.xsOnly ? 'pa-1 mb-1' : 'pa-2 mb-2'"
                      class="text-caption rounded red lighten-5"
                      style="border-left: 4px solid #6c0000;"
                    >
                      <div class="d-flex align-center justify-space-between mb-1">
                        <strong class="primary--text d-flex align-center">
                          <v-icon x-small color="primary" class="mr-1">mdi-face-mask</v-icon>
                          Tämän tehtäväluokan maskisuositus:
                        </strong>
                        <v-chip x-small color="primary" dark class="font-weight-black">
                          {{ currentHappisuositus.virtaus }}
                        </v-chip>
                      </div>
                      <div class="font-weight-bold grey--text text--darken-4 pl-1">
                        {{ currentHappisuositus.maski }}
                      </div>
                      <div class="text-caption grey--text text--darken-3 pl-1 mt-1">
                        {{ currentHappisuositus.ohje }}
                      </div>
                    </div>
                    <div
                      v-else-if="med.maskType"
                      :class="$vuetify.breakpoint.xsOnly ? 'pa-1 mb-1' : 'pa-2 mb-1'"
                      class="text-caption rounded red lighten-5"
                    >
                      <strong class="primary--text d-flex align-center">
                        <v-icon x-small color="primary" class="mr-1">mdi-face-mask</v-icon>
                        Maskin tyyppi:
                      </strong>
                      <div
                        class="grey--text text--darken-4 pl-2 mt-1"
                        style="white-space: pre-line;"
                      >
                        {{ med.maskType }}
                      </div>
                    </div>
                    <div class="text-caption mb-1">
                      <strong>Käyttöaihe:</strong> {{ med.indications }}
                    </div>
                    <div class="text-caption red--text text--darken-2 mb-1">
                      <strong>Vasta-aiheet / ehdot:</strong>
                      {{ med.contraindications }}
                    </div>
                    <div class="text-caption grey--text text--darken-2">
                      <strong>Huomio:</strong> {{ med.notes }}
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-sheet>
          </v-tab-item>

          <!-- 5. TURVALLISUUS & HUOMIOT -->
          <v-tab-item>
            <v-sheet :class="$vuetify.breakpoint.xsOnly ? 'pa-1' : 'pa-2'">
              <div
                :class="$vuetify.breakpoint.xsOnly ? 'text-caption font-weight-bold mb-1' : 'text-subtitle-2 font-weight-bold mb-2'"
                class="amber--text text--darken-4 d-flex align-center"
              >
                <v-icon small color="amber darken-4" class="mr-1"
                  >mdi-shield-alert</v-icon
                >
                Työturvallisuus ja huomioitavaa
              </div>
              <v-list dense class="pa-0 mb-2">
                <v-list-item
                  v-for="(item, idx) in ohje.safety"
                  :key="idx"
                  :class="$vuetify.breakpoint.xsOnly ? 'px-1 py-1 rounded mb-1' : 'px-2 rounded-lg mb-1'"
                  class="amber lighten-5"
                >
                  <v-list-item-icon :class="$vuetify.breakpoint.xsOnly ? 'mr-1 my-1' : 'mr-2 my-2'">
                    <v-icon small color="amber darken-4">mdi-alert</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content :class="$vuetify.breakpoint.xsOnly ? 'py-0' : 'py-1'">
                    <span :class="$vuetify.breakpoint.xsOnly ? 'text-caption' : 'text-body-2'" class="font-weight-medium">{{
                      item
                    }}</span>
                  </v-list-item-content>
                </v-list-item>
              </v-list>

              <template
                v-if="ohje.erityishuomiot && ohje.erityishuomiot.length > 0"
              >
                <div
                  :class="$vuetify.breakpoint.xsOnly ? 'text-caption font-weight-bold mb-1' : 'text-subtitle-2 font-weight-bold mb-1'"
                  class="grey--text text--darken-3"
                >
                  Erityishuomiot ja ohjeet
                </div>
                <v-card outlined :class="$vuetify.breakpoint.xsOnly ? 'rounded pa-1' : 'rounded-lg pa-2'" class="grey lighten-5">
                  <div
                    v-for="(note, idx) in ohje.erityishuomiot"
                    :key="idx"
                    class="text-caption mb-1"
                    :class="{
                      'font-weight-bold':
                        note.startsWith('-') || note.startsWith('V ='),
                    }"
                  >
                    {{ note }}
                  </div>
                </v-card>
              </template>
            </v-sheet>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-divider />

      <v-card-actions :class="$vuetify.breakpoint.xsOnly ? 'px-1 py-1' : 'px-4 py-2'" class="d-flex flex-wrap align-center">
        <v-btn
          text
          :small="!$vuetify.breakpoint.xsOnly"
          :x-small="$vuetify.breakpoint.xsOnly"
          color="primary"
          class="font-weight-bold"
          @click="showIsbarDialog = true"
        >
          <v-icon left small>mdi-phone-incoming</v-icon>
          iSBAR-konsultaatiomalli
        </v-btn>

        <v-spacer />

        <v-btn
          color="primary"
          dark
          :small="!$vuetify.breakpoint.xsOnly"
          :x-small="$vuetify.breakpoint.xsOnly"
          class="font-weight-bold rounded-lg"
          :to="{ path: '/ensivaste-ohjeet', query: { code: activeCode } }"
        >
          Avaa kaikki EVY-ohjeet
          <v-icon right small>mdi-open-in-new</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-alert v-else type="info" outlined dense class="ma-2">
      Tehtäväkoodille {{ code }} ei löydy suoraa EVY-ohjetta.
      <v-btn
        x-small
        text
        color="primary"
        class="ml-2 font-weight-bold"
        to="/ensivaste-ohjeet"
      >
        Selaa kaikkia ohjeita
      </v-btn>
    </v-alert>

    <!-- GCS Wizard Dialog -->
    <gcs-wizard v-model="showGcsDialog" />

    <!-- FPSS Stroke Wizard Dialog -->
    <fpss-wizard v-model="showFpssDialog" />

    <v-dialog
      v-model="showIsbarDialog"
      max-width="650px"
      :fullscreen="$vuetify.breakpoint.xsOnly"
    >
      <v-card class="rounded-xl">
        <v-card-title class="primary white--text py-3">
          <v-icon color="white" class="mr-2">mdi-phone-incoming</v-icon>
          <span class="white--text">iSBAR-konsultaatio- ja raportointimalli</span>
          <v-spacer />
          <v-btn icon dark @click="showIsbarDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="text-body-2 mb-3">
            Käytä tätä mallia konsultoidessasi ensihoitolääkäriä (ESA 00) tai
            kenttäjohtajaa (L4), tai raportoidessasi suullisesti saapuvalle ambulanssille:
          </div>

          <v-timeline dense align-top>
            <v-timeline-item
              v-for="step in isbarMalli.steps"
              :key="step.letter"
              color="primary"
              small
            >
              <div class="font-weight-bold text-subtitle-2">
                {{ step.name }}
              </div>
              <div class="text-body-2 grey--text text--darken-3">
                {{ step.content }}
              </div>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>

  <!-- DIALOG MODE -->
  <v-dialog
    v-else
    :value="value"
    max-width="850px"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card
      :class="$vuetify.breakpoint.xsOnly ? 'rounded-0' : 'rounded-xl'"
      class="overflow-hidden"
      style="position: relative;"
    >
      <div
        class="d-flex justify-end pa-2"
        style="position: absolute; right: 4px; top: 4px; z-index: 10;"
      >
        <v-btn
          icon
          color="grey darken-2"
          title="Sulje"
          @click="$emit('input', false)"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text :class="$vuetify.breakpoint.xsOnly ? 'pa-1 pt-8' : 'pa-3 pt-8'">
        <!-- Render the inline version inside dialog without redundant title -->
        <ensivaste-quick-guide :code="code" inline hide-title />
      </v-card-text>

      <v-divider />
      <v-card-actions class="justify-end py-2 px-3">
        <v-btn text color="primary" @click="$emit('input', false)">
          Sulje
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import GcsWizard from "./GcsWizard";
import FpssWizard from "./FpssWizard";
import {
  getOhjeByCode,
  getHappisuositus,
  ENSIVASTE_LAAKKEET,
  ISBAR_MALLI,
} from "@/assets/ensivasteOhjeet";

export default {
  name: "EnsivasteQuickGuide",
  components: {
    GcsWizard,
    FpssWizard,
  },
  props: {
    code: {
      type: String,
      default: "",
    },
    value: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: 0,
      showGcsDialog: false,
      showFpssDialog: false,
      showIsbarDialog: false,
      isbarMalli: ISBAR_MALLI,
    };
  },
  computed: {
    activeCode() {
      if (!this.code) return "";
      return this.code.trim();
    },
    isStroke() {
      if (!this.ohje) return this.activeCode === "706";
      return (
        this.ohje.code === "706" ||
        (this.ohje.codes && this.ohje.codes.includes("706")) ||
        this.ohje.category === "neurological"
      );
    },
    ohje() {
      return getOhjeByCode(this.activeCode);
    },
    currentHappisuositus() {
      return getHappisuositus(this.ohje || this.activeCode);
    },
    relevantMedications() {
      if (!this.ohje) return [];
      const code = this.ohje.code;
      const happiMed = ENSIVASTE_LAAKKEET.find((m) => m.name.includes("happi"));

      let specificMeds = [];
      if (code === "704") {
        specificMeds = ENSIVASTE_LAAKKEET.filter((m) =>
          [
            "ASA (Asetyylisalisyylihappo, Aspirin® / Disperin®)",
            "Dinit® suusumute (Isosorbididinitraatti)",
          ].includes(m.name),
        );
      } else if (code === "773") {
        specificMeds = ENSIVASTE_LAAKKEET.filter((m) =>
          m.name.includes("Adrenaliini"),
        );
      } else if (code === "771" || code === "702") {
        specificMeds = ENSIVASTE_LAAKKEET.filter((m) =>
          m.name.includes("Glukoosigeeli") || m.name.includes("GlucoBooster"),
        );
      }

      return [...specificMeds, ...(happiMed ? [happiMed] : [])];
    },
    hasMedication() {
      return this.relevantMedications.length > 0;
    },
  },
};
</script>

<style scoped>
.evy-guide-card {
  border-left: 6px solid #6c0000 !important;
}

@media (max-width: 600px) {
  .evy-guide-card {
    border-left-width: 4px !important;
  }

  ::v-deep .v-card__text {
    padding: 2px 2px !important;
  }

  ::v-deep .v-tabs-bar {
    height: 38px !important;
  }

  ::v-deep .v-tab {
    min-width: unset !important;
    padding: 0 6px !important;
    font-size: 0.75rem !important;
  }

  ::v-deep .v-list-item {
    min-height: 28px !important;
  }
}
</style>
